<template>
  <div class="fdsg-chart" :class="{ 'fdsg-chart-ok': !isLoading && !error }">
    <div v-if="error" class="alert alert-danger">No data received</div>
    <iframe
      sandbox="allow-same-origin  allow-scripts"
      ref="iframe"
      :src="chartIQSource"
      @load="onLoadIframe"
    ></iframe>
  </div>
</template>

<script>
import moment from "moment";
import "moment-timezone";
import {
  ChartContent,
  cloneResult
} from "@fds/wm-id-chart-iq-content/src/content";
import { ChartIQContentDataProvider } from "@fds/wm-typescript-data-providers";
import {
  locale,
  dateFormatter,
  mdg2Client,
  numberFormatter,
  eventBus
} from "../util/instances";
import * as utils from "../util/chart";
import merge from "deepmerge";
import { defaultTimeSpans } from "../util/chart";

global.moment = moment;

/**
 * Format for price values
 *
 * @param value
 * @returns {string}
 */
const formatValue = value => {
  return numberFormatter.format(value, "BIG_NUMBER");
};

/**
 * Format for date time
 *
 * @param value
 * @param intraday true or false
 * @returns {string}
 */
const formatDateTime = (value, intraday) => {
  return dateFormatter.format(value, intraday ? "DATETIME" : "DATE");
};

const CHART_IQ_SOURCE_PATH = "assets/chartiq/0.2.3/fdsg/fdsg-chartiq.html";

const emptyObject = function() {
  return {};
};

export default {
  name: "AdvancedChart",
  props: {
    channel: {
      type: String,
      required: true,
      default: ""
    },
    idNotation: {
      type: Number,
      required: true
    },
    userProfile: {
      type: String,
      required: false,
      default: "chartist"
    },
    selectedSpan: {
      type: String,
      required: false,
      default: "1D"
    },
    proxyPathPrefix: {
      type: String,
      default: null
    },
    config: {
      type: Object,
      required: false,
      default: emptyObject
    },
    theme: {
      type: Object,
      required: false,
      default: emptyObject
    }
  },
  data() {
    return {
      isLoading: true,
      error: false
    };
  },
  computed: {
    chartIQSource() {
      if (!this.proxyPathPrefix) {
        return CHART_IQ_SOURCE_PATH;
      }

      return this.proxyPathPrefix + "/" + CHART_IQ_SOURCE_PATH;
    }
  },
  created() {
    this.dataProvider = new ChartIQContentDataProvider(mdg2Client, {
      API_PRICES_QUALITY: "DLY",
      FUNC_FORMAT_VALUE: formatValue,
      FUNC_FORMAT_DATE_TIME: formatDateTime
    });
  },
  methods: {
    /**
     * Notation Event Subscription
     */
    getInitialConfig() {
      const userProfile = this.userProfile;
      const selected = defaultTimeSpans.find(
        obj => obj.label === this.selectedSpan
      );
      const base = selected ? utils[selected.span].base : "today";
      const multiplier = selected ? utils[selected.span].multiplier : 1;
      return {
        userProfile,
        spanBase: base,
        spanMultiplier: multiplier,
        maintainPeriodicity: base === "today"
      };
    },
    /**
     * Modifies the Config in the Localstorage so that a changed conf will load
     */
    modifyLocalstorageConfig(conf) {
      const userProfile = this.userProfile;
      let lsKey = "";
      let config = conf;
      for (let [key, value] of Object.entries(localStorage)) {
        if (key.includes(userProfile)) {
          lsKey = key;
        }
      }
      if (lsKey) {
        localStorage.removeItem(lsKey);
      }
      return config;
    },
    /**
     * Notation Event Subscription
     */
    initNotationEventBus() {
      const engine = this.engine;
      const event = `${this.channel}:changeNotation`;
      if (this.channel) {
        eventBus.$on(event, function(notation) {
          engine.idNotation = notation;
        });
      }
    },
    /**
     * Benchmark Event Subscription
     */
    initBenchmarkEventBus() {
      const event = `${this.channel}:changeBenchmark`;
      const addBenchmark = this.addBenchmark;
      if (this.channel) {
        eventBus.$on(event, function(benchmark) {
          if (benchmark) {
            console.log(benchmark.notation, benchmark.color);
            addBenchmark(benchmark.notation, benchmark.color);
          }
        });
      }
    },
    addBenchmark(idNotation, color) {
      if (idNotation) {
        this.engine.addBenchmark(idNotation, color);
      }
    },
    onLoadIframe: function() {
      const initialConfig = this.getInitialConfig();
      const mergedConfig = this.config
        ? merge(initialConfig, this.config)
        : initialConfig;
      const iframe = this.$refs.iframe;
      const config = this.modifyLocalstorageConfig(mergedConfig);
      this.engine = new ChartContent(iframe, config);

      this.engine.attachDataHandler(this.dataProvider);
      this.engine
        .init({
          idNotation: this.idNotation,
          uniqueId: this.idNotation + this.userProfile,
          language: locale,
          theme: "light",
          defaultPriceRelativeInstrument: this.idNotation
        })
        .then(() => {
          if (this.theme) {
            const helper = new iframe.contentWindow.CIQ.ThemeHelper({
              stx: iframe.contentWindow.UIContext.stx
            });
            helper.settings = merge(helper.settings, this.theme);
            helper.update();
          }
          iframe.style.visibility = "visible";
          this.isLoading = false;
        })
        .catch(() => {
          this.error = true;
          this.isLoading = false;
        });

      this.initNotationEventBus();
      this.initBenchmarkEventBus();
    }
  }
};
</script>

<style lang="scss" scoped>
.fdsg-chart {
  position: relative;

  &.fdsg-chart-ok {
    min-height: 600px;
    width: 100%;
    box-shadow: 2px 2px 5px #ddd;
  }

  > iframe {
    width: 100%;
    height: 100%;
    position: absolute;
    display: block;
    margin: 0;
    border: 0;
    visibility: hidden;
  }
}
</style>
